import {Component, EventEmitter, Input, OnInit, Output, OnDestroy} from '@angular/core';
import {AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {RangeType} from '@constants/pages/user-defined-page/range-type';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {SettingsService} from '@services/administration/settings/settings.service';

@Component({
    selector: 'scitis-line-widget-settings-time-range',
    templateUrl: './line-widget-settings-time-range.component.html',
    styleUrls: ['./line-widget-settings-time-range.component.scss'],
    standalone: false
})
export class LineWidgetSettingsTimeRangeComponent implements OnInit, OnDestroy {

  @Input() timePeriod = {timeValue: 0, rangeType: RangeType.MINUTE};
  @Output() formIsValid = new EventEmitter<boolean>(false);
  @Output() timePeriodChanged: EventEmitter<{timeValue: number, rangeType: RangeType}> = new EventEmitter<{timeValue: number, rangeType: RangeType}>();

  timeRangeSettingsForm: UntypedFormGroup = new UntypedFormGroup({});
  timeUnits: string[] = [RangeType.MINUTE, RangeType.HOUR];
  influxDbEnabled = false;
  isLoadingStreamingToInfluxEnabled: boolean = false;

  private MINUTE_IN_MILLIS = 60 * 1000;
  private HOUR_IN_MILLIS = this.MINUTE_IN_MILLIS * 60;
  private DAY_IN_MILLIS = this.HOUR_IN_MILLIS * 24;

  private $destroy = new Subject<boolean>();
  
  readonly valid = 'VALID';

  constructor(private settingsService: SettingsService) { }
  
  get formControls() {
    return this.timeRangeSettingsForm.controls;
  }
  
  ngOnInit(): void {
    if (this.timePeriod.rangeType === RangeType.TIME || this.timePeriod.rangeType === RangeType.LAST_UNIT) {
      this.timePeriod.rangeType = RangeType.MINUTE;
    }

    this.generateTimeRangeSettingsForm();
    this.isStreamingToInfluxEnabled();
  }

  ngOnDestroy(): void {
    this.$destroy.next(true);
    this.$destroy.complete();
  }

  private generateTimeRangeSettingsForm() {
    this.timeRangeSettingsForm = new UntypedFormGroup({
      timeInputValue: new UntypedFormControl(this.timePeriod.timeValue === 0 ? '' : this.timePeriod.timeValue, [Validators.required]),
      timeUnit: new UntypedFormControl({value: this.timePeriod.timeValue === 0 ? '': this.timePeriod.rangeType, disabled: true}, [Validators.required]),
    }, this.timeRangeValidator());

    this.formControls.timeInputValue.valueChanges.pipe(takeUntil(this.$destroy)).subscribe(result => {
      this.timePeriod.timeValue = result;
      if (this.timePeriod.timeValue > 0) {
        this.timePeriodChanged.emit({timeValue: this.timePeriod.timeValue, rangeType: this.timePeriod.rangeType});
      }
    });

    this.formControls.timeUnit.valueChanges.pipe(takeUntil(this.$destroy)).subscribe(result => {
      switch (result) {
        case RangeType.DAY:
          this.timePeriod.rangeType = RangeType.DAY;
          break;
        case RangeType.HOUR:
          this.timePeriod.rangeType = RangeType.HOUR;
          break;
        case RangeType.MINUTE:
          this.timePeriod.rangeType = RangeType.MINUTE;
          break;
      }
      if (this.timePeriod.timeValue > 0) {
        this.timePeriodChanged.emit({timeValue: this.timePeriod.timeValue, rangeType: this.timePeriod.rangeType});
      }
    });

    this.timeRangeSettingsForm.statusChanges.pipe(takeUntil(this.$destroy)).subscribe(statusChange => {
      this.formIsValid.emit(!this.isLoadingStreamingToInfluxEnabled && (statusChange === this.valid));
    });
  }

  timeRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let mills = 0;
      const MAX_RANGE_INFLUX = 30 * this.DAY_IN_MILLIS;
      const MAX_RANGE_DATASTORE = 120 * this.MINUTE_IN_MILLIS;
      const maxTimeMills = this.influxDbEnabled ? MAX_RANGE_INFLUX : MAX_RANGE_DATASTORE;
      let maxTimeInCurrentUnit;
      let timeUnit = 'minutes';
      switch (control.value.timeUnit) {
        case RangeType.DAY:
          mills = control.value.timeInputValue * this.DAY_IN_MILLIS;
          maxTimeInCurrentUnit = Math.round(maxTimeMills / (this.DAY_IN_MILLIS) * 1000) / 1000;
          timeUnit = 'days';
          break;
        case RangeType.HOUR:
          mills = control.value.timeInputValue * this.HOUR_IN_MILLIS;
          maxTimeInCurrentUnit = maxTimeMills / this.HOUR_IN_MILLIS;
          timeUnit = 'hours';
          break;
        case RangeType.MINUTE:
          mills = control.value.timeInputValue * this.MINUTE_IN_MILLIS;
          maxTimeInCurrentUnit = maxTimeMills / this.MINUTE_IN_MILLIS;
          break;
      }
      const errorString = `The configured range of ${control.value.timeInputValue} ${timeUnit} is bigger than the allowed maximum of ${maxTimeInCurrentUnit} ${timeUnit}!`;
      return maxTimeMills < mills ? {timeToLong: errorString} : null;
    };
  }

  private isStreamingToInfluxEnabled() {
    this.isLoadingStreamingToInfluxEnabled = true;
    this.settingsService.isInfluxStreamingEnabled().pipe(takeUntil(this.$destroy)).subscribe(result => {
      this.influxDbEnabled = result;
      if (this.influxDbEnabled) {
        this.timeUnits = [RangeType.MINUTE, RangeType.HOUR, RangeType.DAY];
      }
      this.isLoadingStreamingToInfluxEnabled = false;
      this.formControls?.timeUnit?.enable();
      this.timeRangeSettingsForm.updateValueAndValidity();
      this.formIsValid.emit(this.timeRangeSettingsForm.errors === null);
  });
  }
}
